import React from 'react';
import { Badge, Button, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FaHouseMedical } from 'react-icons/fa6';
import {
  MdCancel,
  MdHistory,
  MdNotificationsActive,
  MdOutlineVolumeUp,
  MdTaskAlt,
} from 'react-icons/md';

const STATUS_UI = {
  calling: {
    label: 'Chamando',
    variant: 'warning',
    textColor: 'text-dark',
  },
  arrived: {
    label: 'Na sala',
    variant: 'info',
    textColor: 'text-dark',
  },
  done: {
    label: 'Atendido',
    variant: 'primary',
    textColor: 'text-white',
  },
  expired: {
    label: 'Expirado',
    variant: 'danger',
    textColor: 'text-white',
  },
  canceled: {
    label: 'Cancelado',
    variant: 'danger',
    textColor: 'text-white',
  },
};

/**
 * Componente Wrapper para Tooltips estáveis (sem tremer)
 */
const HintButton = ({ text, children, disabled }) => {
  const renderTooltip = (props) => (
    <Tooltip id={`tooltip-${text.replace(/\s+/g, '-').toLowerCase()}`} {...props}>
      {text}
    </Tooltip>
  );

  return (
    <OverlayTrigger placement="top" delay={{ show: 200, hide: 50 }} overlay={renderTooltip}>
      {/* 
        A div com d-inline-block garante que o tooltip funcione 
        mesmo se o botão dentro dela estiver 'disabled' 
      */}
      <span className="d-inline-block" style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
        {children}
      </span>
    </OverlayTrigger>
  );
};

export default function PainelDoMedico({
  agenda,
  callState,
  onCall,
  onRecall,
  onArrive,
  onDone,
  onCancel,
  onViewHistory,
  isProntuario,
}) {
  const status = callState?.status;
  const isLoading = Boolean(callState?.isLoading);
  const callId = callState?.callId;
  const hasActiveCall = Boolean(callId);

  // Estados derivados para controle preciso dos botões
  const isDone = status === 'done';
  const isExpired = status === 'expired';
  const isArrived = status === 'arrived';
  const isCanceled = status === 'canceled';

  // A chamada está em andamento ativo
  const isCallActive = hasActiveCall && (status === 'calling' || isArrived);

  // Regras de desabilitação
  const disableCall = isLoading || !agenda?.CODPAC || isCallActive;
  const disableRecall = isLoading || !isCallActive;
  const disableArrive =
    isLoading || !hasActiveCall || isArrived || isDone || isExpired || isCanceled;
  const disableDone = isLoading || !hasActiveCall || isDone || isExpired || isCanceled;
  const disableCancel = isLoading || !hasActiveCall || isCanceled;
  const disableHistory = isLoading || !agenda?.CODAGENDA;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="d-flex justify-content-center align-items-center mb-1">
        <div
          className="fw-bold text-primary"
          style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
        >
          PAINEL DE CHAMADAS
        </div>
      </div>

      <div className="d-flex justify-content-center gap-1 mb-1">
        {/* Chamar Paciente */}
        {!isProntuario && (
          <>
            <HintButton text="Chamar o paciente no painel de chamadas" disabled={disableCall}>
              <Button
                size="sm"
                variant="outline-success"
                disabled={disableCall}
                style={disableCall ? { pointerEvents: 'none' } : {}}
                onClick={() => onCall?.(agenda)}
              >
                <MdOutlineVolumeUp size={16} />
              </Button>
            </HintButton>

            {/* Rechamar */}
            <HintButton text="Rechamar o paciente" disabled={disableRecall}>
              <Button
                size="sm"
                variant="outline-warning"
                disabled={disableRecall}
                style={disableRecall ? { pointerEvents: 'none' } : {}}
                onClick={() => onRecall?.(agenda, callState)}
              >
                <MdNotificationsActive size={16} />
              </Button>
            </HintButton>

            {/* Cancelar */}
            <HintButton text="Cancelar chamada" disabled={disableCancel}>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={disableCancel}
                style={disableCancel ? { pointerEvents: 'none' } : {}}
                onClick={() => onCancel?.(agenda, callState)}
              >
                <MdCancel size={16} />
              </Button>
            </HintButton>
          </>
        )}

        {/* Confirmar Chegada */}
        <HintButton text="Confirmar chegada do paciente na Sala" disabled={disableArrive}>
          <Button
            size="sm"
            variant="outline-info"
            disabled={disableArrive}
            style={disableArrive ? { pointerEvents: 'none' } : {}}
            onClick={() => onArrive?.(agenda, callState)}
          >
            <FaHouseMedical size={14} />
          </Button>
        </HintButton>

        {/* Finalizar Atendimento */}
        <HintButton text="Finalizar atendimento" disabled={disableDone}>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={disableDone}
            style={disableDone ? { pointerEvents: 'none' } : {}}
            onClick={() => onDone?.(agenda, callState)}
          >
            <MdTaskAlt size={16} />
          </Button>
        </HintButton>

        {/* Histórico */}
        <HintButton text="Consultar histórico das chamadas" disabled={disableHistory}>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={disableHistory}
            style={disableHistory ? { pointerEvents: 'none' } : {}}
            onClick={() => onViewHistory?.(agenda, callState)}
          >
            <MdHistory size={16} />
          </Button>
        </HintButton>
      </div>

      {/* Badge de status operacional e indicador de carregamento */}
      <div
        className="d-flex flex-column align-items-center justify-content-center gap-1"
        style={{ minHeight: '22px' }}
      >
        {status && STATUS_UI[status] && (
          <Badge
            bg={STATUS_UI[status].variant}
            className={`${STATUS_UI[status].textColor} p-1 px-2 fw-semibold`}
            style={{ fontSize: '0.7rem' }}
          >
            Status: <span className="fw-normal">{STATUS_UI[status].label}</span>
          </Badge>
        )}

        {isLoading && (
          <div className="py-1">
            <Spinner animation="border" size="sm" variant="secondary" />
          </div>
        )}
      </div>
    </div>
  );
}
